import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, XCircle, Loader2, Crown, Zap, Star, Infinity, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';

const API = `${window.location.origin}/api`;

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const sessionId = searchParams.get('session_id');
  const hasShownSuccessToast = React.useRef(false);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    fetchCurrentSubscription();
  }, [sessionId]);

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/check`, { withCredentials: true });
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 3000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please contact support if payment was successful.');
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await axios.get(
        `${API}/subscription/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.status === 'paid') {
        if (!hasShownSuccessToast.current) {
          hasShownSuccessToast.current = true;
          toast.success('Subscription activated successfully!');
        }
        setCheckingStatus(false);
        setTimeout(() => navigate('/dashboard?subscribed=true'), 2000);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error checking payment status';
      
      if (error.response?.status === 404) {
        toast.error('Payment session not found. Please try again or contact support.');
        setCheckingStatus(false);
      } else if (error.response?.status >= 500 || !error.response) {
        console.log(`Retrying... attempt ${attempts + 1}/${maxAttempts}`);
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error(errorMsg);
        setCheckingStatus(false);
      }
    }
  };

  const handleSubscribe = async (planType) => {
    setLoadingPlan(planType);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { origin_url: originUrl, plan_type: planType },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create checkout session';
      toast.error(message);
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/subscription/portal`,
        { origin_url: originUrl },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to open subscription portal';
      toast.error(message);
      setLoadingPortal(false);
    }
  };

  // Check if user has an active paid subscription (not trial)
  const hasPaidSubscription = currentSubscription?.has_subscription && !currentSubscription?.is_trial;

  if (checkingStatus) {
    return (
      <div className="min-h-screen theme-bg flex items-center justify-center">
        <Card className="card-glow p-8 text-center max-w-md">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Processing Payment</h2>
          <p className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>Please wait while we activate your subscription...</p>
        </Card>
      </div>
    );
  }

  const plans = [
    {
      id: 'base',
      name: 'Base',
      price: 9.99,
      icon: Star,
      color: 'amber',
      popular: false,
      features: [
        { text: '6 courses per month', included: true, highlight: true },
        { text: '6 debates per month', included: true, highlight: true },
        { text: 'Unused credits roll over', included: true, highlight: true },
        { text: 'Unlimited chat with Professor Clio', included: true, highlight: false },
        { text: 'Progress tracking', included: true, highlight: false },
        { text: 'AI-generated quizzes', included: true, highlight: false },
        { text: 'Normal complexity level only', included: true, highlight: false },
        { text: 'Scholar complexity level', included: false, highlight: false },
      ],
      description: 'Perfect for casual learners'
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 19.99,
      icon: Crown,
      color: 'orange',
      popular: true,
      features: [
        { text: '15 courses per month', included: true, highlight: true },
        { text: '15 debates per month', included: true, highlight: true },
        { text: 'Unused credits roll over', included: true, highlight: true },
        { text: 'Unlimited chat with Professor Clio', included: true, highlight: false },
        { text: 'Normal complexity level', included: true, highlight: false },
        { text: 'Scholar complexity level', included: true, highlight: false },
        { text: 'Progress tracking', included: true, highlight: false },
        { text: 'AI-generated quizzes', included: true, highlight: false },
      ],
      description: 'Best for dedicated students'
    },
    {
      id: 'platinum',
      name: 'Platinum',
      price: 29.99,
      icon: Infinity,
      color: 'purple',
      popular: false,
      features: [
        { text: 'Unlimited courses', included: true, highlight: true },
        { text: 'Unlimited debates', included: true, highlight: true },
        { text: '3 course daily soft cap', included: true, highlight: false },
        { text: 'Unlimited chat with Professor Clio', included: true, highlight: false },
        { text: 'Normal complexity level', included: true, highlight: false },
        { text: 'Scholar complexity level', included: true, highlight: false },
        { text: 'Progress tracking', included: true, highlight: false },
        { text: 'AI-generated quizzes', included: true, highlight: false },
      ],
      description: 'For power users and educators'
    }
  ];

  const getColorClasses = (color, isDark) => {
    const colors = {
      amber: {
        border: isDark ? 'border-amber-700' : 'border-amber-300',
        header: isDark ? 'bg-amber-900/30' : 'bg-gradient-to-r from-amber-100 to-amber-50',
        icon: 'text-amber-500',
        button: 'bg-amber-600 hover:bg-amber-700',
        title: isDark ? 'text-amber-100' : 'text-amber-900',
        badge: 'bg-amber-500'
      },
      orange: {
        border: isDark ? 'border-orange-600 ring-2 ring-orange-500' : 'border-orange-400 ring-2 ring-orange-400',
        header: isDark ? 'bg-orange-900/40' : 'bg-gradient-to-r from-orange-100 to-amber-100',
        icon: 'text-orange-500',
        button: 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700',
        title: isDark ? 'text-orange-100' : 'text-orange-900',
        badge: 'bg-orange-500'
      },
      purple: {
        border: isDark ? 'border-purple-700' : 'border-purple-300',
        header: isDark ? 'bg-purple-900/30' : 'bg-gradient-to-r from-purple-100 to-purple-50',
        icon: 'text-purple-500',
        button: 'bg-purple-600 hover:bg-purple-700',
        title: isDark ? 'text-purple-100' : 'text-purple-900',
        badge: 'bg-purple-500'
      }
    };
    return colors[color] || colors.amber;
  };

  return (
    <div className="min-h-screen theme-bg py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className={`mb-6 ${isDarkMode ? 'text-amber-300 hover:text-amber-100' : 'text-amber-700 hover:text-amber-900'}`}
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="text-center mb-10">
          <Crown className="w-14 h-14 text-amber-600 mx-auto mb-4" />
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
            {hasPaidSubscription ? 'Manage Your Plan' : 'Choose Your Plan'}
          </h1>
          <p className={`text-base sm:text-lg ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
            All plans billed monthly. Cancel anytime.
          </p>
        </div>

        {/* Existing Subscriber Banner */}
        {hasPaidSubscription && (
          <div className="w-fit mx-auto mb-8">
            <button
              onClick={handleManageSubscription}
              disabled={loadingPortal}
              className={`text-sm ${isDarkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'} underline cursor-pointer disabled:opacity-50`}
              data-testid="manage-subscription-btn"
            >
              {loadingPortal ? 'Loading...' : 'Manage your active subscription'}
            </button>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto mb-8">
          {plans.map((plan) => {
            const colors = getColorClasses(plan.color, isDarkMode);
            const IconComponent = plan.icon;
            
            return (
              <Card 
                key={plan.id}
                className={`card-glow ${colors.border} shadow-xl relative flex flex-col rounded-xl ${plan.popular ? 'scale-105 z-10' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <div className={`${colors.badge} text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg flex items-center gap-1`}>
                      <Zap className="w-4 h-4" />
                      MOST POPULAR
                    </div>
                  </div>
                )}

                <CardHeader className={`text-center ${colors.header} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${plan.popular ? 'pt-8' : ''} rounded-t-xl`}>
                  <div className="flex justify-center mb-2">
                    <IconComponent className={`w-10 h-10 ${colors.icon}`} />
                  </div>
                  <CardTitle className={`text-2xl ${colors.title} mb-1`}>{plan.name}</CardTitle>
                  <div className={`text-4xl font-bold ${colors.title} my-3`}>
                    ${plan.price}<span className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>/mo</span>
                  </div>
                  <CardDescription className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-5 flex-1 flex flex-col">
                  <div className="space-y-3 mb-6 flex-1">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        {feature.included ? (
                          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-500" />
                        ) : (
                          <XCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                        )}
                        <span className={`text-sm ${
                          feature.included 
                            ? (feature.highlight 
                                ? (isDarkMode ? 'text-white font-medium' : 'text-gray-900 font-medium') 
                                : (isDarkMode ? 'text-gray-300' : 'text-gray-700'))
                            : (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                        }`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loadingPlan !== null}
                    className={`w-full ${colors.button} text-white py-5 text-base font-semibold`}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {loadingPlan === plan.id ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Get {plan.name}
                        <IconComponent className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Rollover Explanation */}
        <div className={`max-w-2xl mx-auto mb-8 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-amber-50'}`}>
          <h3 className={`text-center font-semibold mb-2 ${isDarkMode ? 'text-amber-200' : 'text-amber-900'}`}>
            How Rollover Works
          </h3>
          <p className={`text-center text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
            Base and Pro plans include rollover credits. Unused courses and debates carry over to the next month 
            (up to your monthly limit). Generate 3 courses this month? You'll have 9 available next month on Base!
          </p>
        </div>

        <p className={`text-center text-sm mb-6 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
          Secure payment via Stripe. Cancel anytime. No hidden fees.
        </p>

        {/* Feature Comparison */}
        <div className={`max-w-4xl mx-auto rounded-lg overflow-hidden ${isDarkMode ? 'bg-gray-800/30' : 'bg-white'} shadow-lg`}>
          <div className={`p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-amber-100'}`}>
            <h3 className={`text-center font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
              All Plans Include
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
            {[
              'Unlimited AI Chat',
              'Debate Performance Feedback',
              'Progress Tracking',
              'AI-Generated Quizzes',
              'Detailed Feedback',
              'Course Certificates',
              'Debate Room Access',
              'Mobile Friendly'
            ].map((feature, index) => (
              <div key={index} className="flex items-start space-x-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
